import { useMemo, useState } from 'react'

import { useStripe } from '@stripe/react-stripe-js'
import type { Stripe } from '@stripe/stripe-js'
import type { Field } from 'factorial-form'

import asyncAction from 'modules/core/lib/asyncAction'

import usePlans from 'modules/billing/components/Table/PricingsRow/usePlans'
import SubscriptionForm from 'modules/billing/lib/forms/SubscriptionForm'
import subscriptions from 'modules/billing/store/collections/subscriptions'
import type Plan from 'modules/billing/store/models/Plan'
import type { AvailableFamily } from 'modules/billing/store/models/Plan'

interface SubscriptionCreationCallbackProps {
  stripe: Stripe | null
  status: string
  clientSecret: string
  onPaymentActionRequired: () => void
  onPaymentSuccessful: () => void
}

const onSubscriptionCreation = async ({
  stripe,
  status,
  clientSecret,
  onPaymentActionRequired,
  onPaymentSuccessful,
}: SubscriptionCreationCallbackProps) => {
  if (stripe && status === 'incomplete') {
    const result = await stripe.confirmSepaDebitPayment(clientSecret, {
      save_payment_method: true,
      setup_future_usage: 'off_session',
    })

    if (result.paymentIntent && result.paymentIntent.status === 'succeeded') {
      onPaymentSuccessful()
    } else {
      onPaymentActionRequired()
    }
  } else {
    onPaymentSuccessful()
  }
}

interface Props {
  loading: boolean
  family: AvailableFamily
  isYearly: boolean
  onFail: () => void
  onPaymentActionRequired: () => void
  onPaymentSuccessful: () => void
}

const useCreateSubscription = ({
  loading,
  family,
  isYearly,
  onFail,
  onPaymentActionRequired,
  onPaymentSuccessful,
}: Props) => {
  const stripe = useStripe()
  const plans: any = usePlans(loading)
  const [selectedPlan, setPlan] = useState<Plan | null | undefined>(null)

  const initialPlan = useMemo<Plan | null | undefined>(() => {
    if (loading) return null

    const period = isYearly ? 'year' : 'month'
    const plan = plans[family][period]
    setPlan(plan)

    return plan
  }, [loading, family, isYearly, plans])

  const subscriptionForm = useMemo(() => {
    return new SubscriptionForm({
      plan: initialPlan?.attributes.id,
      coupon: initialPlan?.coupon,
    })
  }, [initialPlan])

  const onChangePricingPeriod = (planField: Field) => {
    const planFamilyKey = Object.keys(plans).find((key) => key === selectedPlan?.family)

    if (!planFamilyKey) return

    const plan = plans[planFamilyKey][planField.value]
    subscriptionForm.get('plan').set(plan.get('id'))
    setPlan(plan)
  }

  const onChangePricingPlan = (planField: Field) => {
    const planFamilyKey = Object.keys(plans).find((key) => key === planField.value)

    if (!planFamilyKey) return

    const intervalKey = Object.keys(plans[planFamilyKey]).find(
      (interval) => interval === selectedPlan?.attributes.interval
    )

    if (!intervalKey) return

    const plan = plans[planFamilyKey][intervalKey]

    if (!plan) return

    subscriptionForm.get('plan').set(plan.get('id'))
    setPlan(plan)
  }

  const createSubscription = async () => {
    const { success, value } = await asyncAction(() => subscriptionForm.create(subscriptions), {
      successFlash: false,
    })

    if (success) {
      await onSubscriptionCreation({
        stripe,
        status: value.status,
        clientSecret: value.client_secret,
        onPaymentActionRequired,
        onPaymentSuccessful,
      })
    } else {
      onFail()
    }
  }

  return {
    selectedPlan: selectedPlan || initialPlan,
    onChangePricingPeriod,
    onChangePricingPlan,
    subscriptionForm,
    createSubscription,
  }
}

export default useCreateSubscription
